import React, { useState, useEffect } from 'react';
import { PharmacyTabType } from '.';
import axios from 'axios';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {
  PharmacyService,
  getAdminConfigVar,
  formatDate
} from '@avicennapharmacy/managemymeds-shared';
import Select from 'react-select';

export default ({ pharmacy, setPharmacy }: PharmacyTabType) => {
  const [services, setServices] = useState<PharmacyService[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(false);
  const [requestNewService, setRequestNewService] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [serviceRequests, setServiceRequests] = useState<any[]>([]);

  const servicesByIds = pharmacy.servicesOfferedByIds || [];

  const availableServices = services.map(s => ({
    value: s.id,
    label: s.name
  }));

  const servicesProvided = availableServices.filter(s => servicesByIds.includes(s.value));

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(false);
      //const result: any = await axios.get(url);
      const [servicesRes, requestsRes] = await Promise.all([
        axios.get(`${getAdminConfigVar('pharmacyServicesEndpoint')}/${pharmacy.id}`),
        axios.get(`${getAdminConfigVar('serviceRequestsEndpoint')}/${pharmacy.id}`)
      ]);
      setServices(servicesRes.data);
      setServiceRequests(requestsRes.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setError(true);
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Spinner variant="primary" animation="border" role="status" />;
  }

  return (
    <>
      <h4 className="mb-4 mt-5">Services Offered</h4>
      <Row className="mb-5">
        <Col sm={8}>
          <Select
            isMulti
            options={availableServices}
            value={servicesProvided}
            onChange={(selected: any) => {
              if (!selected) {
                selected = [];
              }
              setPharmacy({
                ...pharmacy,
                servicesOffered: Array.isArray(selected)
                  ? selected.map(s => s.label)
                  : [selected.label],
                servicesOfferedByIds: Array.isArray(selected)
                  ? selected.map(s => s.value)
                  : [selected.value]
              });
            }}
          />
        </Col>
      </Row>
      {pharmacy.ownershipType !== 'Owned' ? (
        <>
          <h4 className="mb-4">Add a New Service</h4>
          <Row className="mb-5">
            <Col sm={6}>
              <Form.Control
                type="text"
                placeholder="New service"
                value={requestNewService}
                onChange={(e: any) => setRequestNewService(e.target.value)}
              />
            </Col>
            <Col sm={2}>
              <Button
                onClick={async () => {
                  try {
                    setSendingRequest(true);
                    var newService = (
                      await axios.post<PharmacyService>(
                        getAdminConfigVar('memberAddServiceEndpoint'),
                        {
                          pharmacyId: pharmacy.id,
                          serviceName: requestNewService
                        }
                      )
                    ).data;
                    setServices([...services, newService]);
                    setPharmacy({
                      ...pharmacy,
                      servicesOffered: [...pharmacy.servicesOffered, newService.name],
                      servicesOfferedByIds: [...pharmacy.servicesOfferedByIds, newService.id]
                    });
                    setSendingRequest(false);
                    setShowModal(false);
                    setRequestNewService('');
                  } catch {
                    setError(true);
                  }
                }}
                disabled={requestNewService.trim() === ''}
              >
                <FontAwesomeIcon icon="plus-circle" /> Add
              </Button>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <h4 className="mb-4">Request a New Service</h4>
          <Row>
            <Col sm={8}>
              <p>
                If a new service your pharmacy wants to offer is not available in the list, please
                submit a request for this. Once approved, it will be available for all pharmacies to
                select.
              </p>
            </Col>
          </Row>
          <Row className="mb-5">
            <Col sm={6}>
              <Form.Control
                type="text"
                placeholder="New service"
                value={requestNewService}
                onChange={(e: any) => setRequestNewService(e.target.value)}
              />
            </Col>
            <Col sm={2}>
              <Button onClick={() => setShowModal(true)} disabled={requestNewService.trim() === ''}>
                <FontAwesomeIcon icon="plus-circle" /> Request
              </Button>
            </Col>
          </Row>
          {serviceRequests.length > 0 && (
            <>
              <h4 className="mb-4">Requested Services</h4>
              <Row>
                <Col sm={10}>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Service Name</th>
                        <th>Requested By</th>
                        <th>Requested On</th>
                        <th>Reviewed By</th>
                        <th>Reviewed On</th>
                        <th className="text-center">Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceRequests.map((req, i) => (
                        <tr key={i}>
                          <td>{req.requestedServiceName}</td>
                          <td>{req.requestedByEmail}</td>
                          <td>{formatDate(req.requestDt, 'dateAndTime')}</td>
                          <td>{req.reviewedByEmail}</td>
                          <td>{formatDate(req.reviewedDt, 'dateAndTime')}</td>
                          <td className="text-center">
                            {req.approved ? (
                              <FontAwesomeIcon icon="check-circle" className="text-primary" />
                            ) : (
                              <FontAwesomeIcon icon="times-circle" className="text-danger" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to request the new service <strong>{requestNewService}</strong>?
          </p>
          <p>An admin will review the request once submitted.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          {sendingRequest ? (
            <Button variant="primary" disabled>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="sr-only">Sending...</span>
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  setSendingRequest(true);
                  await axios.post(getAdminConfigVar('serviceRequestEndpoint'), {
                    pharmacyId: pharmacy.id,
                    serviceName: requestNewService
                  });
                  setSendingRequest(false);
                  setShowModal(false);
                  setRequestNewService('');
                } catch {
                  setError(true);
                }
              }}
            >
              Send Request
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
